import React from 'react';
import { ArrowRight, Building2, Home, ShieldCheck, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { redLightAvailability } from '../data/availability';

const RedLightSystems = () => {
  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const pathways = [
    {
      icon: Home,
      title: 'Home Systems',
      text: 'Practical red-light configurations being assessed for convenient home use.'
    },
    {
      icon: Building2,
      title: 'Studio & Clinic',
      text: 'Professional systems for fitness, wellness and body-sculpting environments.'
    },
    {
      icon: Sparkles,
      title: 'EFAM Custom Finish',
      text: 'Supplier discussions are underway for EFAM styling, branding and equipment finishes.'
    }
  ];

  return (
    <section id="red-light" className="py-24 bg-gradient-to-b from-black via-red-950/20 to-black relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[700px] h-[500px] bg-red-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span
              className="inline-block px-4 py-1.5 rounded-full border border-red-400/50 bg-red-500/10 text-red-300 text-xs font-bold uppercase tracking-[0.2em] mb-5"
              data-testid="red-light-status"
            >
              {redLightAvailability.label}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-5">
              Professional <span className="text-red-400">Red-Light Systems</span>
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed">
              EFAM is working directly with suppliers to finalise a range of premium red-light equipment for homes, studios and clinics. We will publish confirmed specifications, wavelengths, finishes and pricing only after each model has been validated.
            </p>
            <p className="mt-4 text-base text-red-300 font-semibold">
              {redLightAvailability.note}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {pathways.map(({ icon: Icon, title, text }) => (
              <div key={title} className="glass-card rounded-2xl p-7 border border-red-500/20">
                <div className="w-12 h-12 bg-red-500/15 rounded-xl flex items-center justify-center border border-red-400/40 mb-5">
                  <Icon className="text-red-400" size={24} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{title}</h3>
                <p className="text-gray-400 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>

          <div className="glass-card rounded-2xl p-7 md:p-9 border border-red-400/40 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <ShieldCheck className="text-red-400 flex-shrink-0 mt-1" size={28} />
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Register your interest</h3>
                <p className="text-gray-400 max-w-2xl">
                  Tell us whether you need a home, studio or commercial system. We will contact you when the confirmed EFAM range and launch pricing are ready.
                </p>
              </div>
            </div>
            <Button
              className="bg-red-500 hover:bg-red-400 text-white font-bold rounded-full px-8 py-6 whitespace-nowrap"
              onClick={scrollToContact}
              data-testid="red-light-register-interest"
            >
              Register Interest
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </div>

          <p className="text-center text-xs text-gray-500 mt-7">
            Specifications and availability are subject to final supplier confirmation. Red-light equipment is not presented as a medical treatment.
          </p>
        </div>
      </div>
    </section>
  );
};

export default RedLightSystems;
